import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import _orderBy from 'lodash/orderBy';
import DataGrid, { SortColumn } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

// Store
import {
  $allSurveys,
  $indicatorSelected,
  $indicatorsObject,
  $surveySelected,
} from '../../store/slices/admin2EstimatesSlice';
import { $adminSelected } from '../../store/slices/sidebarMapFiltersSlice';

// Utils
import { getIndicatorKey } from '../../utils/indicators.utils';
import { getAdmin2dataFilteredSorted } from '../../utils/admin2Data.utils';

// Configs
import { COLUMN_CHARACTERISTIC, COLUMN_COUNTRY, COLUMN_VALUE, COLUMN_SURVEY } from '../../configs/tools.configs';
import { ADMIN__Admin1, ADMIN__Admin2, DATA_VALUE_PRECISION } from '../../configs/map.configs';

// Services
import { getGisRestApiSurveyData } from '../../services/getSurveyData.service';

// Styles
import './Admin2Table.scss';

const COLUMNS_ADMIN_1 = [
  { sortable: false, key: COLUMN_COUNTRY, name: 'COUNTRY' },
  { sortable: true, key: COLUMN_CHARACTERISTIC, name: 'CHARACTERISTIC' },
  { sortable: true, key: COLUMN_SURVEY, name: 'SURVEY' },
  { sortable: true, key: COLUMN_VALUE, name: 'VALUE' },
];

function getColumnComparatorForAdmin1(sortColumn: string) {
  switch (sortColumn) {
    case COLUMN_COUNTRY:
    case COLUMN_CHARACTERISTIC:
    case COLUMN_SURVEY:
      return (a: any, b: any) => {
        return a[sortColumn].localeCompare(b[sortColumn]);
      };
    case COLUMN_VALUE:
      return (a: any, b: any) => {
        return a[sortColumn] - b[sortColumn];
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

/**
 * Handles the value formatting for columns. If the value is an integer, it is returned as is. Otherwise, it is rounded to the specified precision.
 *
 * @param {number} value - The value to be formatted.
 * @returns {number | string} The formatted value.
 */
function handleColumnValue(value: any): number | string {
  if (typeof value === 'number') {
    if (value === 0) {
      return 0;
    }

    if (value.toFixed(0).toString().length === 1) {
      return value.toFixed(2);
    } else {
      return value.toFixed(DATA_VALUE_PRECISION);
    }
  } else {
    return '';
  }
}

const COLUMNS_ADMIN_2 = [
  { sortable: true, key: 'adm1_name', name: 'Admin 1 Name' },
  { sortable: true, key: 'adm1_estimate', name: 'Admin 1 Estimate' },
  { sortable: true, key: 'adm2_name', name: 'Admin 2 Name' },
  { sortable: true, key: 'adm2_estimate', name: 'Admin 2 Estimate' },
  { sortable: true, key: 'lower_ci', name: 'Uncertainty lower bound' },
  { sortable: true, key: 'upper_ci', name: 'Uncertainty upper bound' },
  { sortable: true, key: 'width_ci', name: 'Uncertainty width' },
  { sortable: true, key: 'Population_at_risk', name: 'Population' },
];

function getColumnComparatorForAdmin2(sortColumn: string) {
  switch (sortColumn) {
    case 'adm1_name':
    case 'adm2_name':
      return (a: any, b: any) => {
        return a[sortColumn].localeCompare(b[sortColumn]);
      };
    case 'adm1_estimate':
    case 'adm2_estimate':
    case 'lower_ci':
    case 'upper_ci':
    case 'width_ci':
    case 'Population_at_risk':
      return (a: any, b: any) => {
        return a[sortColumn] - b[sortColumn];
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function Admin2Table() {
  const indicatorsObject = useSelector($indicatorsObject);
  const indicatorSelected = useSelector($indicatorSelected);
  const surveySelected = useSelector($surveySelected);
  const adminSelected = useSelector($adminSelected);
  const allSurveys = useSelector($allSurveys);

  const [rows, setRows] = useState<any[]>([]);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([
    {
      columnKey: adminSelected === ADMIN__Admin1 ? COLUMN_CHARACTERISTIC : 'adm2_name',
      direction: 'ASC',
    },
  ]);

  function updateAdmin2Rows(indicatorsObject: any) {
    const admin2dataFilteredSorted = getAdmin2dataFilteredSorted(indicatorSelected, allSurveys, surveySelected);
    const admin2dataFilteredSortedColumnPicked = admin2dataFilteredSorted.map((survey: any) => {
      const admin1Attributes = indicatorsObject[survey.attributes.region_id];

      const rowValues = {
        adm1_name: survey.attributes.adm1_name,
        adm1_estimate: admin1Attributes?.Value ? parseFloat(admin1Attributes?.Value) : null,
        adm2_name: survey.attributes.adm2_name,
        adm2_estimate: handleColumnValue(survey.attributes.Value),
        lower_ci: handleColumnValue(survey.attributes.lower_ci),
        upper_ci: handleColumnValue(survey.attributes.upper_ci),
        width_ci: handleColumnValue(survey.attributes.width_ci),
        Population_at_risk: survey.attributes.Population_at_risk,
      };

      return rowValues;
    });

    setRows(admin2dataFilteredSortedColumnPicked);
  }

  useEffect(() => {
    const indicatorKey: string = getIndicatorKey(ADMIN__Admin1);
    const indicatorsObjectByIndicatorKey = indicatorsObject[indicatorKey];

    if (adminSelected === ADMIN__Admin1) {
      if (indicatorsObject && indicatorKey && indicatorsObjectByIndicatorKey) {
        const indicatorsObjectValuesList = Object.values(indicatorsObjectByIndicatorKey);
        const rows = _orderBy(indicatorsObjectValuesList, [COLUMN_CHARACTERISTIC]);
        setRows(rows);
      }
    } else if (adminSelected === ADMIN__Admin2) {
      if (!indicatorsObjectByIndicatorKey && surveySelected) {
        getGisRestApiSurveyData(indicatorSelected, surveySelected).then((surveyData) => {
          const indicatorsObject: any = {};
          surveyData.forEach((survey: any) => {
            indicatorsObject[survey.RegionID] = {
              Value: survey.estimates.toFixed(DATA_VALUE_PRECISION),
            };
          });

          updateAdmin2Rows(indicatorsObject);
        });
      } else {
        updateAdmin2Rows(indicatorsObjectByIndicatorKey);
      }
    }
  }, [indicatorsObject, indicatorSelected, surveySelected]);

  const sortedRowsForAdmin = useMemo(() => {
    if (sortColumns.length === 0) return rows;

    const sortedRows = [...rows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator =
          adminSelected === ADMIN__Admin1
            ? getColumnComparatorForAdmin1(sort.columnKey)
            : getColumnComparatorForAdmin2(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });

    return sortedRows;
  }, [rows, sortColumns]);

  return (
    <section className='Admin2Table row'>
      <div className='col-lg-12 col-md-12'>
        <DataGrid
          className={'Admin2Table__DataGrid'}
          columns={adminSelected === ADMIN__Admin1 ? COLUMNS_ADMIN_1 : COLUMNS_ADMIN_2}
          rows={sortedRowsForAdmin}
          defaultColumnOptions={{
            sortable: true,
          }}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
        />
      </div>
    </section>
  );
}

export default Admin2Table;
